// src/components/ProductCard.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { triggerHaptic } from '../utils/haptics';
// @ts-ignore
import { useText } from '../utils/locales';
import { Product } from '../types';

interface ProductCardProps {
  item: Product;
  quantity: number;
  onAdd: () => void;
  onRemove: () => void;
  onClick: () => void;
}

const CoffeeBeanIcon = ({ active }: { active: boolean }) => (
  <svg 
    viewBox="0 0 64 64" 
    className={`w-2.5 h-2.5 transition-all duration-500 ${active ? 'text-[#6F4E37]' : 'text-tg-hint opacity-20 grayscale'}`}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M48.8,12.2c-10.4-10.4-27.2-10.4-37.6,0c-10.4,10.4-10.4,27.2,0,37.6c10.4,10.4,27.2,10.4,37.6,0 C59.2,39.4,59.2,22.6,48.8,12.2z M17.2,40.8c-2-2-2.9-4.7-2.6-7.3c0.3-2.6,1.6-4.9,3.6-6.6c4-3.4,9.6-3.8,14-1.2l-15,15.1 C17.2,40.8,17.2,40.8,17.2,40.8z M44.6,25.4l-14.1,14c-1.8,1.8-4.2,2.7-6.6,2.6c-2.4-0.1-4.7-1.1-6.4-2.8c-0.1-0.1-0.1-0.1-0.2-0.2 l15-15c2.1-2.1,5.1-3.2,8.1-2.9C43.3,21.5,45.4,23.1,44.6,25.4z" />
  </svg>
);

const ProductCard: React.FC<ProductCardProps> = ({ item, quantity, onAdd, onRemove, onClick }) => {
  const t = useText();

  // CHANGED: Универсальный парсер характеристик
  const tagsString = item.tags ? String(item.tags) : '';
  const rawTags = tagsString.split(',').map(tag => tag.trim()).filter(Boolean);

  const statsKeys = [
    { key: 'крепость', label: 'Крепость' },
    { key: 'обжарка', label: 'Обжарка' },
    { key: 'насыщенность', label: 'Вкус' }, // Сократил для карточки
    { key: 'кислотность', label: 'Кислот.' }
  ];

  const itemStats: {label: string, value: number}[] = [];
  const normalTags: string[] = [];

  rawTags.forEach(tag => {
    let isStat = false;
    for (const stat of statsKeys) {
      const regex = new RegExp(`${stat.key}[:\\s]*([1-5])`, 'i');
      const match = tag.match(regex);
      if (match) {
        itemStats.push({ label: stat.label, value: parseInt(match[1], 10) });
        isStat = true;
        break;
      }
    }
    if (!isStat) normalTags.push(tag);
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-tg-secondary-bg rounded-2xl p-2.5 flex flex-col gap-2 h-full shadow-sm relative active:scale-[0.99] transition-transform"
      onClick={onClick}
    >
      <div className="aspect-square w-full bg-tg-bg rounded-xl overflow-hidden relative">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
        {normalTags.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1 z-10">
            {normalTags.slice(0, 2).map((tag, idx) => (
              <span key={idx} className="bg-black/60 backdrop-blur-sm text-white text-[9px] font-black px-2 py-0.5 rounded-lg shadow-sm uppercase tracking-wider">
                {tag}
              </span>
            ))}
          </div>
        )}
        {quantity > 0 && <div className="absolute top-2 right-2 bg-tg-button text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md z-10">x{quantity}</div>}
      </div>

      <div className="flex flex-col flex-1 px-1">
        <h3 className="text-sm font-bold text-tg-text leading-tight line-clamp-2 mb-1">{item.name}</h3>
        
        {/* CHANGED: Статистика в зернах (столбиком) */}
        {itemStats.length > 0 && (
          <div className="flex flex-col gap-1 mb-2">
            {itemStats.slice(0, 3).map((stat, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="text-[8px] uppercase font-black text-tg-hint w-12 tracking-tighter">{stat.label}:</span>
                <div className="flex gap-[2px]">
                  {[1,2,3,4,5].map(b => <CoffeeBeanIcon key={b} active={b <= stat.value} />)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-1">
          <span className="text-sm font-bold text-tg-text">{Math.floor(item.price)} {t.currency}</span>
          {quantity > 0 ? (
             <div className="flex items-center bg-tg-bg rounded-lg h-8 px-1 shadow-sm" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); triggerHaptic('selection'); }} className="w-7 h-full flex items-center justify-center text-tg-hint active:text-red-500"><Minus size={16} /></button>
                <span className="font-bold text-sm min-w-[14px] text-center">{quantity}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); onAdd(); triggerHaptic('selection'); }} className="w-7 h-full flex items-center justify-center text-tg-button"><Plus size={16} /></button>
             </div>
          ) : (
            <button type="button" onClick={(e) => { e.stopPropagation(); onAdd(); triggerHaptic('light'); }} className="bg-tg-button text-white w-8 h-8 rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all"><Plus size={18} strokeWidth={3} /></button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
